// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import { arcKey, LanguagePair } from '../../../utils/arcRows';
import { Locale } from '../../../utils/locales';
import { describeError as describeApiError } from '../../../utils/serverErrors';
import { ApiError } from '../../../utils/serverErrors';
import {
  terMultiplierFormula,
  terMultiplierWeight,
  weightText,
} from '../../../utils/terMultiplier';
import { BasePrice } from '../BaseRates/baseRates';
import {
  ChangeSetBase,
  ChangeSetSummary,
  DESCRIPTION_REQUIRED_MESSAGE,
  describeModelSaveError,
  findChangeSetToEdit,
  parseArc,
} from '../PricingModels/pricingModels';
import { defaultTerOf, lacksDefaultTers, TerGroup } from '../TerGroups/terGroups';

export const SCOPING_MODELS_ROUTE = '/settings/scopingModels';
export const SCOPING_MODELS_PATH = '/scopingModels';

export const scopingModelRoute = (weightSchemeId: string): string =>
  `${SCOPING_MODELS_ROUTE}/${encodeURIComponent(weightSchemeId)}`;

export const scopingModelPath = (weightSchemeId: string): string =>
  `${SCOPING_MODELS_PATH}/${encodeURIComponent(weightSchemeId)}`;

export const READ_ONLY_NOTE = 'You can view scoping models but not change them.';

export interface MatchPercentPriceModifier {
  name: string;
  lowerBound: number;
  percentage: number;
}

export interface BasePriceModifier {
  // Null for every arc (Any to Any).
  languagePair: LanguagePair | null;
  repetitionPriceModifier: { percentage: number };
  matchPercentPriceModifiers: MatchPercentPriceModifier[];
}

/** The repetition weight, which every model sets as a fixed value. */
export type RepetitionField = 'repetitionWeight';

/** The match bands, each set as a fixed value or a TER multiplier. */
export enum MatchBand {
  ZeroTo49 = 'zeroTo49',
  FiftyTo74 = 'fiftyTo74',
  P75 = 'p75',
  P85 = 'p85',
  P95 = 'p95',
  P100 = 'p100',
  P101 = 'p101',
}

/** The field choosing each match band's entry mode, keyed as `MatchBand` is. */
export enum ModeField {
  ZeroTo49 = 'zeroTo49Mode',
  FiftyTo74 = 'fiftyTo74Mode',
  P75 = 'p75Mode',
  P85 = 'p85Mode',
  P95 = 'p95Mode',
  P100 = 'p100Mode',
  P101 = 'p101Mode',
}

/** The field of each match band's fixed weight, keyed as `MatchBand` is. */
export enum WeightField {
  ZeroTo49 = 'zeroTo49Weight',
  FiftyTo74 = 'fiftyTo74Weight',
  P75 = 'p75Weight',
  P85 = 'p85Weight',
  P95 = 'p95Weight',
  P100 = 'p100Weight',
  P101 = 'p101Weight',
}

/** The field of each match band's TER multiplier, keyed as `MatchBand` is. */
export enum TerMultiplierField {
  ZeroTo49 = 'zeroTo49TerMultiplier',
  FiftyTo74 = 'fiftyTo74TerMultiplier',
  P75 = 'p75TerMultiplier',
  P85 = 'p85TerMultiplier',
  P95 = 'p95TerMultiplier',
  P100 = 'p100TerMultiplier',
  P101 = 'p101TerMultiplier',
}

// Exactly one of each match band's weight and TER multiplier is set; drafts saved before every
// band could take a TER multiplier have no multiplier for the bands from 75%.
export interface Draft
  extends Record<RepetitionField, number>,
    Partial<Record<WeightField | TerMultiplierField, number | null>> {
  weightSchemeId: string;
  name: string;
  description?: string | null;
  // Set when a band uses a TER multiplier only.
  terGroupId?: string | null;
  excludedArcs?: LanguagePair[] | null;
  baseRateVersion?: number | null;
  terGroupVersion?: number | null;
  basePriceModifiers?: BasePriceModifier[] | null;
}

export interface ChangeSet extends ChangeSetBase {
  organizationId: string;
  entityType: string;
  entityId: string;
  entityName: string;
  weightSchemeId: string;
  draft: Draft;
}

// The live fields are null until a change set is approved.
export interface ScopingModelSummary {
  weightSchemeId: string;
  name: string;
  description?: string | null;
  author: string;
  createdAt: string;
  version: number | null;
  liveChangeSetId: string | null;
  liveApprovedAt: string | null;
  liveApprovedBy: string | null;
  // Null when the model has no change set.
  latestChangeSetId: string | null;
  latestChangeSetAt: string | null;
  latestChangeSetAuthor: string | null;
  pendingChangeSetCount: number;
}

export interface ScopingModel extends ScopingModelSummary {
  latestChangeSet: ChangeSet | null;
  // The change set the live version was approved from; null until one is approved.
  liveChangeSet: ChangeSet | null;
  // Newest first.
  changeSets: ChangeSetSummary[];
}

export const TER_MULTIPLIER_LABEL = 'TER multiplier';

// Weights are entered as decimals, 1 being 100%, as Pricing takes them and as the pricing model
// multipliers and TERs are entered.
export const REPETITION: { field: RepetitionField; label: string } = {
  field: 'repetitionWeight',
  label: 'Repetition',
};

/** A band's weight is a fixed value or, per arc, TER + (1 − TER) × a TER multiplier. */
export type EntryMode = 'value' | 'multiplier';

// Both entries of a match band are kept while switching; only the chosen mode's is sent.
export interface FormValues
  extends Record<RepetitionField | WeightField | TerMultiplierField, string>,
    Record<ModeField, EntryMode> {
  name: string;
  description: string;
  terGroupId: string;
  // Arc keys, as built by arcKey.
  excludedArcs: string[];
}

export type FormField = keyof FormValues;

/** The inputs that set the match bands' weights. */
export type BandEntries = Pick<FormValues, ModeField | WeightField | TerMultiplierField>;

export type FieldErrors = Partial<Record<FormField, string>>;

export interface MatchBandFields {
  band: MatchBand;
  label: string;
  mode: ModeField;
  weight: WeightField;
  terMultiplier: TerMultiplierField;
}

const matchBand = (band: keyof typeof MatchBand, label: string): MatchBandFields => ({
  band: MatchBand[band],
  label,
  mode: ModeField[band],
  weight: WeightField[band],
  terMultiplier: TerMultiplierField[band],
});

/** Every band but repetition, in match order. */
export const MATCH_BANDS: MatchBandFields[] = [
  matchBand('ZeroTo49', '0–49%'),
  matchBand('FiftyTo74', '50–74%'),
  matchBand('P75', '75–84%'),
  matchBand('P85', '85–94%'),
  matchBand('P95', '95–99%'),
  matchBand('P100', '100%'),
  matchBand('P101', '101%'),
];

/** The 0–49% and 50–74% bands, which the models list shows as columns. */
export const ZERO_TO_74_BANDS = MATCH_BANDS.slice(0, 2);

export const emptyForm = (): FormValues => {
  const values = {
    name: '',
    description: '',
    terGroupId: '',
    repetitionWeight: '',
    excludedArcs: [],
  } as Partial<FormValues>;
  MATCH_BANDS.forEach(band => {
    values[band.mode] = 'value';
    values[band.weight] = '';
    values[band.terMultiplier] = '';
  });
  return values as FormValues;
};

const bandOfField = (field: FormField): MatchBandFields | undefined =>
  MATCH_BANDS.find(
    band => field === band.mode || field === band.weight || field === band.terMultiplier
  );

/** Whether any band uses a TER multiplier, so the model needs a TER group. */
export const usesMultiplier = (values: BandEntries): boolean =>
  MATCH_BANDS.some(band => values[band.mode] === 'multiplier');

/** The field of a band's chosen entry: its TER multiplier or its fixed weight. */
export const entryField = (
  values: BandEntries,
  band: MatchBandFields
): WeightField | TerMultiplierField =>
  values[band.mode] === 'multiplier' ? band.terMultiplier : band.weight;

export interface UpdateScopingModelRequest
  extends Record<RepetitionField, number>,
    Partial<Record<WeightField | TerMultiplierField, number>> {
  terGroupId?: string;
  excludedArcs: LanguagePair[];
}

export interface CreateScopingModelRequest extends UpdateScopingModelRequest {
  name: string;
  description: string;
}

/**
 * Every input but the name and description, which cannot change after create, as an update sends
 * them to replace the whole draft. Only each band's chosen entry is sent, and the TER group only
 * when a band uses a TER multiplier.
 */
export const toUpdateRequest = (values: FormValues): UpdateScopingModelRequest => {
  const entries = MATCH_BANDS.reduce(
    (request, band) => ({
      ...request,
      [entryField(values, band)]: Number(values[entryField(values, band)]),
    }),
    {}
  );
  const request: UpdateScopingModelRequest = {
    ...entries,
    ...(usesMultiplier(values) ? { terGroupId: values.terGroupId } : {}),
    repetitionWeight: Number(values.repetitionWeight),
    excludedArcs: values.excludedArcs.map(parseArc),
  };
  return request;
};

/** A create also names and describes the model, which cannot change after. */
export const toCreateRequest = (values: FormValues): CreateScopingModelRequest => ({
  name: values.name.trim(),
  description: values.description.trim(),
  ...toUpdateRequest(values),
});

const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;
const MAX_DECIMALS = 2;

export const WEIGHT_CONSTRAINT = 'Required. A number from 0 to 1, up to 2 decimal places.';

export const validateWeight = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed) || Number(trimmed) > 1) {
    return 'Enter a number from 0 to 1.';
  }
  // Trailing zeros do not count, as Pricing strips them.
  if ((trimmed.split('.')[1] || '').replace(/0+$/, '').length > MAX_DECIMALS) {
    return `Use at most ${MAX_DECIMALS} decimal places.`;
  }
  return undefined;
};

/** A weight's error while it is typed; an empty weight is not yet an error. */
export const typedWeightError = (value: string): string | undefined =>
  value.trim() === '' ? undefined : validateWeight(value);

/** Whether the chosen entry of every match band is valid. */
export const entriesComplete = (values: BandEntries): boolean =>
  MATCH_BANDS.every(band => validateWeight(values[entryField(values, band)]) === undefined);

/** Whether every band, repetition included, is valid, as Pricing requires them all. */
export const weightsComplete = (values: FormValues): boolean =>
  entriesComplete(values) && validateWeight(values.repetitionWeight) === undefined;

/**
 * Whether Create or Save can be clicked: every weight is valid and, if a band uses a TER
 * multiplier, a TER group is chosen.
 */
export const readyToSubmit = (values: FormValues): boolean =>
  weightsComplete(values) && (!usesMultiplier(values) || values.terGroupId !== '');

export const TER_GROUP_REQUIRED_MESSAGE = 'Choose a TER group.';

/** Checks what Pricing would certainly reject; the name and description only on a create. */
export const validateForm = (values: FormValues, creating: boolean): FieldErrors => {
  const errors: FieldErrors = {};
  if (creating && values.name.trim() === '') {
    errors.name = 'Enter a name.';
  }
  if (creating && values.description.trim() === '') {
    errors.description = DESCRIPTION_REQUIRED_MESSAGE;
  }
  MATCH_BANDS.forEach(band => {
    const field = entryField(values, band);
    errors[field] = validateWeight(values[field]);
  });
  if (usesMultiplier(values) && values.terGroupId === '') {
    errors.terGroupId = TER_GROUP_REQUIRED_MESSAGE;
  }
  errors.repetitionWeight = validateWeight(values.repetitionWeight);
  return errors;
};

export const hasFieldErrors = (errors: FieldErrors): boolean => Object.values(errors).some(Boolean);

const isSet = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined;

const valueText = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * A weight Pricing stored, to 2 decimal places as the preview shows weights; one stored with more
 * decimal places, which Pricing does not accept, is shown as stored rather than rounded.
 */
export const storedWeightText = (weight: number): string => {
  try {
    return weightText(String(weight));
  } catch (error) {
    return String(weight);
  }
};

const isMultiplierBand = (draft: Draft, band: MatchBandFields): boolean =>
  isSet(draft[band.terMultiplier]);

/** Whether any of a draft's bands uses a TER multiplier. */
export const isMultiplierDraft = (draft: Draft): boolean =>
  MATCH_BANDS.some(band => isMultiplierBand(draft, band));

/**
 * The form for editing a model, from the draft `findChangeSetToEdit` picks; the name, and the
 * description when the model has one, are the model's, which cannot change.
 */
export const formFromModel = (model: ScopingModel): FormValues => {
  const changeSet = findChangeSetToEdit(model);
  const draft = changeSet && changeSet.draft;
  if (!draft) {
    return { ...emptyForm(), name: model.name, description: model.description || '' };
  }
  const values: FormValues = {
    ...emptyForm(),
    name: model.name,
    description: model.description || draft.description || '',
    terGroupId: draft.terGroupId || '',
    excludedArcs: (draft.excludedArcs || []).map(arcKey),
  };
  MATCH_BANDS.forEach(band => {
    values[band.mode] = isMultiplierBand(draft, band) ? 'multiplier' : 'value';
    values[band.weight] = valueText(draft[band.weight]);
    values[band.terMultiplier] = valueText(draft[band.terMultiplier]);
  });
  values.repetitionWeight = valueText(draft.repetitionWeight);
  return values;
};

/** How a draft's band reads: its TER multiplier, or the fixed value to 2 decimal places. */
export const entryText = (draft: Draft, band: MatchBandFields): string => {
  const weight = draft[band.weight];
  if (isMultiplierBand(draft, band)) {
    return `${TER_MULTIPLIER_LABEL} ${draft[band.terMultiplier]}`;
  }
  return isSet(weight) ? storedWeightText(weight) : '';
};

/** How the detail page reads a draft's band: a TER multiplier also shows its formula. */
export const detailEntryText = (draft: Draft, band: MatchBandFields): string =>
  isMultiplierBand(draft, band)
    ? `${entryText(draft, band)} (${terMultiplierFormula(String(draft[band.terMultiplier]))})`
    : entryText(draft, band);

export interface PreviewRow extends Record<MatchBand, string> {
  languagePair: LanguagePair;
  // Only a TER multiplier weights an arc by its TER.
  ter?: string;
}

/** The live base-rate arcs that are not excluded, the arcs Pricing generates weights for. */
const weightedArcs = (basePrices: BasePrice[], excludedArcs: string[]): LanguagePair[] => {
  const excluded = new Set(excludedArcs);
  return basePrices
    .map(basePrice => basePrice.languagePair)
    .filter(languagePair => !excluded.has(arcKey(languagePair)));
};

/**
 * The match-band weights of each live base-rate arc that is not excluded, as Pricing generates
 * them: a fixed value as is, a TER multiplier at the arc's TER in the TER group (its own entry,
 * else the group's bilingual or monolingual default TER), each to 2 decimal places. `terGroup` is
 * needed only when a band uses a multiplier. Null when that group lacks either default TER, since
 * Pricing then generates no weights from it.
 */
export const previewOf = (
  values: BandEntries & Pick<FormValues, 'excludedArcs'>,
  basePrices: BasePrice[],
  terGroup: TerGroup | null
): PreviewRow[] | null => {
  if (usesMultiplier(values) && terGroup && lacksDefaultTers(terGroup)) {
    return null;
  }
  const terByArc = new Map(
    (terGroup ? terGroup.ters : []).map(entry => [arcKey(entry.languagePair), entry.ter])
  );
  return weightedArcs(basePrices, values.excludedArcs).map(languagePair => {
    const arcTer = terByArc.get(arcKey(languagePair));
    const ter =
      usesMultiplier(values) && terGroup
        ? String(arcTer === undefined ? defaultTerOf(terGroup, languagePair) : arcTer)
        : undefined;
    const weightOf = (band: MatchBandFields): string => {
      const entry = values[entryField(values, band)].trim();
      return values[band.mode] === 'multiplier' && ter !== undefined
        ? terMultiplierWeight(ter, entry)
        : weightText(entry);
    };
    const row = { languagePair, ...(ter === undefined ? {} : { ter }) } as PreviewRow;
    MATCH_BANDS.forEach(band => {
      row[band.band] = weightOf(band);
    });
    return row;
  });
};

const FIELDS: FormField[] = [
  'name',
  'description',
  ...MATCH_BANDS.flatMap(band => [band.weight, band.terMultiplier]),
  'terGroupId',
  'repetitionWeight',
  'excludedArcs',
];

const shown = (field: FormField, values: FormValues): boolean => {
  const band = bandOfField(field);
  if (band) {
    return (field === band.terMultiplier) === (values[band.mode] === 'multiplier');
  }
  return field !== 'terGroupId' || usesMultiplier(values);
};

const ACCESS_DENIED_MESSAGE = 'You do not have permission to manage scoping models.';

export const describeError = (error: ApiError): string[] =>
  describeApiError(error, ACCESS_DENIED_MESSAGE);

export interface SaveFailure {
  fieldErrors: FieldErrors;
  formErrors: string[];
}

/** Puts each validation error on the field its path names, if the form shows that field. */
export const describeSaveError = (
  error: ApiError,
  request: UpdateScopingModelRequest,
  values: FormValues,
  locales: Locale[] | null
): SaveFailure =>
  describeModelSaveError(error, {
    fields: FIELDS,
    shown: field => shown(field, values),
    excludedArcs: request.excludedArcs,
    locales,
    rejectedMessage: 'Pricing rejected the scoping model.',
    accessDeniedMessage: ACCESS_DENIED_MESSAGE,
  });
